/**
 * Custom Add-to-Cart Button Synchronization Manager
 * Manages state synchronization across all custom add-to-cart buttons
 * (.product__custom-add-to-cart, .modal-add-to-cart, .sticky-add-to-cart)
 */

// Prevent duplicate class declarations
if (typeof window.CustomAddToCartSyncManager === 'undefined') {
  window.CustomAddToCartSyncManager = class CustomAddToCartSyncManager {
  constructor() {
    this.buttons = new Map();
    this.currentState = {
      isLoading: false,
      isDisabled: true,
      buttonText: 'Select Products',
      isSubscription: false,
      selectedProducts: []
    };
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupButtons();
        this.handleProductSelectionChange(); // Initialize with current product selection state
        
        // Additional scan after a short delay to catch any late-rendered buttons
        setTimeout(() => {
          this.setupButtons();
          this.handleProductSelectionChange();
        }, 200);
      });
    } else {
      this.setupButtons();
      this.handleProductSelectionChange(); // Initialize with current product selection state
      
      // Additional scan after a short delay to catch any late-rendered buttons
      setTimeout(() => {
        this.setupButtons();
        this.handleProductSelectionChange();
      }, 200);
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  setupButtons() {
    // Find all custom add-to-cart buttons (including hidden ones)
    const buttonSelectors = [
      '.product__custom-add-to-cart .custom-add-to-cart-btn',
      '.modal-add-to-cart .custom-add-to-cart-btn',
      '.sticky-add-to-cart .custom-add-to-cart-btn'
    ];

    // Clear existing buttons to avoid duplicates
    this.buttons.clear();

    buttonSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach((button, index) => {
        // Generate unique ID based on context and index to avoid conflicts
        const context = this.getButtonContext(button);
        const uniqueId = `${context}-btn-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Always use our generated unique ID
        const buttonId = uniqueId;
        button.setAttribute('data-sync-id', buttonId); // Store our ID as data attribute
        
        // Only add if not already tracked
        if (!this.buttons.has(buttonId)) {
          const buttonData = {
            element: button,
            context: context,
            btnText: button.querySelector('.btn-text'),
            btnLoading: button.querySelector('.btn-loading')
          };
          this.buttons.set(buttonId, buttonData);
        }
      });
    });
    
    // Initial state update
    this.updateAllButtons();
  }

  getButtonContext(button) {
    if (button.closest('.modal-add-to-cart')) return 'modal';
    if (button.closest('.sticky-add-to-cart')) return 'sticky';
    return 'main';
  }

  setupEventListeners() {
    // Listen for subscription changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('subscription__checkbox')) {
        this.handleSubscriptionChange();
      }
      
      // Listen for subscription select changes
      if (e.target.classList.contains('subscription__select')) {
        this.handleSubscriptionSelectChange(e.target);
      }
    });

    // Listen for product selection changes
    document.addEventListener('productSelection:changed', () => {
      this.handleProductSelectionChange();
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.handleCartUpdate();
    });

    // Setup mutation observer for dynamically added buttons
    this.setupMutationObserver();
  }

  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const newButtons = node.querySelectorAll('.custom-add-to-cart-btn');
            if (newButtons.length > 0) {
              setTimeout(() => this.setupButtons(), 100);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  handleSubscriptionChange() {
    // Get all subscription checkboxes - allow independent states
    const subscriptionCheckboxes = document.querySelectorAll('.subscription__checkbox');
    
    let isSubscription = false;
    
    // Check if any checkbox is checked
    subscriptionCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        isSubscription = true;
      }
    });
    
    // Don't sync all checkboxes - allow independent toggle behavior
    // Each checkbox can now be checked/unchecked independently
    
    // Update select dropdowns visibility
    const selectWrappers = document.querySelectorAll('.subscription__select-wrapper');
    
    selectWrappers.forEach((wrapper) => {
      if (isSubscription) {
        wrapper.style.display = 'block';
        wrapper.classList.add('show');
      } else {
        wrapper.style.display = 'none';
        wrapper.classList.remove('show');
      }
    });
    
    this.currentState.isSubscription = isSubscription;
    this.updateButtonText();
    this.updateAllButtons();
  }

  handleSubscriptionSelectChange(changedSelect) {
    // Sync all subscription selects to the same value
    const subscriptionSelects = document.querySelectorAll('.subscription__select');
    const selectedValue = changedSelect.value;
    
    subscriptionSelects.forEach(select => {
      if (select !== changedSelect) {
        select.value = selectedValue;
      }
    });
    
    this.updateButtonText();
    this.updateAllButtons();
  }

  handleProductSelectionChange() {
    // Get selected products from ProductSelectionManager
    let selectedProducts = [];
    if (window.productSelectionManager && window.productSelectionManager.getSelectedProducts) {
      selectedProducts = window.productSelectionManager.getSelectedProducts();
    }

    this.currentState.selectedProducts = selectedProducts;
    this.currentState.isDisabled = selectedProducts.length === 0;
    
    // Always call updateButtonText to handle both product selection and subscription state
    this.updateButtonText();
    this.updateAllButtons();
  }

  handleCartUpdate() {
    // Reset loading state after cart update
    if (this.currentState.isLoading) {
      this.setLoadingState(false);
      this.setSuccessState(true);
      
      setTimeout(() => {
        this.setSuccessState(false);
        this.updateAllButtons();
      }, 2000);
    }
  }

  updateButtonText() {
    // If no products selected, show select products message
    if (this.currentState.selectedProducts.length === 0) {
      this.currentState.buttonText = 'Select Products';
      return;
    }
    
    // If products are selected, show appropriate text based on subscription state
    if (this.currentState.isSubscription) {
      this.currentState.buttonText = 'Subscribe & Save';
    } else {
      this.currentState.buttonText = 'Checkout';
    }
  }

  setLoadingState(isLoading) {
    this.currentState.isLoading = isLoading;
    this.updateAllButtons();
  }

  setSuccessState(isSuccess) {
    this.buttons.forEach((buttonData) => {
      const { element, btnText } = buttonData;
      if (isSuccess) {
        element.classList.add('success');
        if (btnText) btnText.textContent = 'Added!';
      } else {
        element.classList.remove('success');
      }
    });
  }

  updateAllButtons() {
    this.buttons.forEach((buttonData, buttonId) => {
      this.updateButton(buttonId);
    });
  }

  updateButton(buttonId) {
    const buttonData = this.buttons.get(buttonId);
    if (!buttonData) return;

    const { element, btnText, btnLoading, context } = buttonData;
    if (!element || !btnText) return;

    // Update disabled state
    if (this.currentState.isDisabled) {
      element.classList.add('disabled');
      element.setAttribute('data-disabled', 'true');
    } else {
      element.classList.remove('disabled');
      element.removeAttribute('data-disabled');
    }

    // Update loading state
    if (this.currentState.isLoading) {
      element.classList.add('loading');
      if (btnText) btnText.style.display = 'none';
      if (btnLoading) btnLoading.style.display = 'flex';
    } else {
      element.classList.remove('loading');
      if (btnText) btnText.style.display = 'block';
      if (btnLoading) btnLoading.style.display = 'none';
    }

    // Update button text based on context
    if (btnText && !this.currentState.isLoading) {
      let displayText = this.currentState.buttonText;
      
      // Special text for modal context
      if (context === 'modal' && !this.currentState.isDisabled) {
        displayText = 'Checkout';
      }
      
      btnText.textContent = displayText;
    }
  }

  // Public method to handle add to cart action
  handleAddToCart(button, sectionId) {
    // Prevent double clicks
    if (this.currentState.isLoading) return;

    // Check if products are selected
    if (this.currentState.selectedProducts.length === 0) {
      if (window.productSelectionManager && window.productSelectionManager.openProductSelection) {
        // Find the first available slot
        const quantityOptions = document.querySelectorAll('.quantity-option');
        let availableSlot = null;
        
        for (let i = 0; i < quantityOptions.length; i++) {
          const card = quantityOptions[i];
          const hasProduct = card.querySelector('.quantity-option__product-info');
          if (!hasProduct) {
            availableSlot = i + 1;
            break;
          }
        }
        
        if (availableSlot) {
          window.productSelectionManager.openProductSelection(availableSlot);
        } else {
          alert('All product slots are filled. Please clear a slot to add more products.');
        }
      } else {
        alert('Please select at least one product.');
      }
      return;
    }



    // Check subscription state from any checkbox
    const subscriptionCheckboxes = document.querySelectorAll('.subscription__checkbox');
    const subscriptionSelects = document.querySelectorAll('.subscription__select');
    const isSubscription = Array.from(subscriptionCheckboxes).some(checkbox => checkbox.checked);

    let sellingPlanId = null;
    if (isSubscription) {
      // Get selling plan from any select that has a value
      for (const select of subscriptionSelects) {
        if (select.value) {
          sellingPlanId = parseInt(select.value);
          break;
        }
      }
      
      // Sync all selects to the same value
      if (sellingPlanId) {
        subscriptionSelects.forEach(select => {
          select.value = sellingPlanId.toString();
        });
      }
    }

    // Set loading state
    this.setLoadingState(true);

    // Prepare cart data
    const cartData = {
      items: this.currentState.selectedProducts.map((product) => {
        const item = {
          id: parseInt(product.id),
          quantity: 1,
        };

        if (sellingPlanId) {
          item.selling_plan = sellingPlanId;
        }

        return item;
      }),
    };

    // Add to cart via AJAX
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
      .then((response) => response.json())
      .then((data) => {


        // Trigger cart update events
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Try different cart refresh methods
        if (window.theme && window.theme.cartDrawer && window.theme.cartDrawer.refresh) {
          window.theme.cartDrawer.refresh();
        } else if (window.CartDrawer && window.CartDrawer.refresh) {
          window.CartDrawer.refresh();
        } else if (window.cart && window.cart.refresh) {
          window.cart.refresh();
        }

        // Show upsell modal after successful cart addition
        if (typeof showUpsellModal === 'function') {
          showUpsellModal();
        }
      })
      .catch((error) => {
        console.error('Error:', error);

        // Reset loading state on error
        this.setLoadingState(false);
        this.updateAllButtons();

        // Show error message
        alert('Error adding item to cart. Please try again.');
      });
  }

  // Public method to get current state
  getCurrentState() {
    return { ...this.currentState };
  }

  // Public method to force update all buttons
  forceUpdate() {
    this.setupButtons();
    this.handleProductSelectionChange(); // Ensure current state is applied
    this.updateAllButtons();
  }
}



  // Initialize the sync manager
  window.customAddToCartSyncManager = new window.CustomAddToCartSyncManager();

  // Global function for backward compatibility
  window.handleCustomAddToCart = function(button, sectionId) {
    if (window.customAddToCartSyncManager) {
      window.customAddToCartSyncManager.handleAddToCart(button, sectionId);
    }
  };

  // Global function to update button states
  window.updateCustomButtonState = function() {
    if (window.customAddToCartSyncManager) {
      window.customAddToCartSyncManager.handleProductSelectionChange();
    }
  };


}

// If already defined, just reinitialize
else if (window.CustomAddToCartSyncManager && !window.customAddToCartSyncManager) {
  window.customAddToCartSyncManager = new window.CustomAddToCartSyncManager();
}